<template>
  <div data-component="Layout">
    <!-- Header visible seulement si pas dans un lot -->
    <header class="layout-header">
      <section class="tabs-section">
        <ul class="tabs-list">
          <li>
            <div class="primviewwrapper">
              <i class="ico logo" />&nbsp;
              <router-link 
                class="navbar-title" 
                :to="getParentLink"
              >
                <img class="primview-picture" :src="primviewWithoutIconSrc" alt="primview" />
              </router-link>
              <router-link class="version" to="/metrics">
                v{{ version }}
              </router-link>
            </div>
          </li>
          
          <!-- Navigation links (hidden if from lot timeline) -->
          <template v-if="pathname && !isFromLot(pathname)">
            <li>
              <router-link 
                :to="getParentLink" 
                :class="getActiveClass('/parc-immobilier')"
              >
                Parc immobilier
              </router-link>
            </li>
            <!-- <li>
              <router-link 
                :to="getVisitLink" 
                :class="getActiveClass('/visites')"
              >
                Visites
              </router-link>
            </li>
            <li>
              <router-link 
                :to="getActionsLink" 
                :class="getActiveClass('/actions')"
              >
                Actions
              </router-link>
            </li> -->
          </template>
          
          <!-- Export dropdown (only when online and not from lot) -->
          <li v-if="!isOffline && pathname && !isFromLot(pathname)" class="export-dropdown">
            <div 
              class="export-toggle"
              :class="getActiveClass('/export')"
              @click="toggleExportDropdown"
            >
              Export
              <i class="fas fa-chevron-down dropdown-icon" :class="{ 'open': isExportDropdownOpen }"></i>
            </div>
            <div v-if="isExportDropdownOpen" class="dropdown-menu">
              <router-link 
                to="/export/visites" 
                class="dropdown-item"
                @click="closeExportDropdown"
              >
                <i class="fas fa-file-export"></i>&nbsp;
                Visites
              </router-link>
              <router-link 
                v-if="!hidePreChiffrage"
                to="/export/pre-chiffrages" 
                class="dropdown-item"
                @click="closeExportDropdown"
              >
                <i class="fas fa-calculator"></i>&nbsp;
                Pré-chiffrages
              </router-link>
            </div>
          </li>
        </ul>
      </section>
      
      <section>
        <!-- Database Status Button -->
        <div 
          class="api-status-btn" 
          :class="{ 'online': isApiAvailable, 'offline': !isApiAvailable, 'syncing': isSyncingData }"
          @click="handleApiStatusClick"
          :title="isSyncingData
            ? (syncProgress.method === 'export'
                ? `Téléchargement export ${syncProgress.currentEntity || ''}…`
                : `Synchronisation en cours… (${syncProgress.currentEntity || ''})`)
            : 'Gestion de la base de données - Cliquez pour ouvrir'"
        >
          <i v-if="!isSyncingData" class="fas fa-database"></i>
          <i v-else class="fas fa-spinner fa-spin"></i>
          <span v-if="pendingMutationsCount > 0 && !isSyncingData" class="pending-badge">{{ pendingMutationsCount }}</span>
        </div>
        
        <!-- Help guide -->
        <div class="question-picture">
          <a target="_blank" rel="noopener noreferrer" :href="pdfGuideSrc">
            <img :src="questionSvgSrc" alt="guide utilisateur" />
          </a>
        </div>
        
        <!-- Logout -->
        <div @click="handleLogoutClick" class="logout">
          <i class="ico logout" />
        </div>
      </section>
    </header>
    
    <!-- Sync Sidebar -->
    <SyncSidebar
      :isOpen="isSyncSidebarOpen"
      :isApiAvailable="isApiAvailable"
      :pendingMutations="pendingMutations"
      :syncStats="syncStats"
      :isSyncingData="isSyncingData"
      :syncProgress="syncProgress"
      @close="closeSyncSidebar"
      @force-sync="handleForceSync"
      @force-full-resync="handleForceFullResync"
      @reload-mutations="loadPendingMutations"
    />
    
    <!-- Content slot -->
    <slot></slot>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { getVersion, initializeVersion } from '../../utils/cookies'
import questionSvgSrc from './question.svg'
import primviewWithoutIconSrc from './logo-without-icone.svg'
import cloudIcoSrc from './cloud.svg'
import tabIcoSrc from './tab.svg'
import SyncSidebar from './SyncSidebar.vue'
import apiStatusService from '../../services/apiStatusService'
import mutationService from '../../services/mutationService'
import syncService from '../../services/syncService'
import { getEnv } from '../../utils/env'

export default {
  name: 'AppLayout',
  components: {
    SyncSidebar
  },
  props: {
    questionSvg: {
      type: String,
      default: () => questionSvgSrc
    }
  },
  computed: {
    ...mapState({
      isOffline: state => state.initialLoading.offline,
      detectedOffline: state => state.initialLoading.detectedOffline,
      userId: state => state.user?.id || state.login?.user?.id,
      user: state => state.user || state.login?.user || {}
    }),
    
    // Récupérer la version depuis le cookie
    version() {
      return getVersion()
    },
    
    pathname() {
      return this.$route?.path || ''
    },
    
    // Compte des mutations en attente pour le badge
    pendingMutationsCount() {
      return this.pendingMutations.length + (this.syncStats.propertiesWithToSync || 0)
    },
    
    // Function to check if pathname is from lot
    isFromLot() {
      return (pathname) => {
        return /^\/dossier\//.test(pathname)
      }
    },
    
    // Get active class for navigation
    getActiveClass() {
      return (path) => {
        const currentPath = this.pathname || this.$route.path
        // Vérifier si le chemin commence par le path (avec / ou ? après) ou est exactement égal
        return currentPath === path || currentPath.startsWith(path + '/') || currentPath.startsWith(path + '?') ? 'is-active' : ''
      }
    },
    
    // Navigation link builders
    // Les filtres sont gérés dans localStorage par les vues de liste,
    // il n'est plus nécessaire de les passer dans l'URL de navigation.
    getParentLink() {
      return '/parc-immobilier'
    },

    getVisitLink() {
      return '/visites'
    },

    getActionsLink() {
      return '/actions'
    },

    // Masquer le bouton "Pré-chiffrage" selon la variable d'environnement
    hidePreChiffrage() {
      return getEnv('VUE_APP_HIDE_PRE_CHIFFRAGE') === 'true'
    }
  },
  methods: {
    ...mapActions({
      logout: 'login/logout',
      toggleOfflineAction: 'initialLoading/toggleOffline'
    }),
    
    // Helper pour récupérer le teamId depuis sessionStorage
    getStoredTeamIdMethod() {
      try {
        const stored = sessionStorage.getItem('preservedRouteParams')
        if (stored) {
          const parsed = JSON.parse(stored)
          return parsed.team || ''
        }
      } catch (error) {
        // Ignorer les erreurs
      }
      return ''
    },
    
    // Event handlers
    async handleLogoutClick() {
      try {
        await this.logout()
        // Rediriger vers la page de logout après déconnexion
        this.$router.push('/logout')
      } catch (error) {
        console.error('Erreur lors de la déconnexion:', error)
      }
    },
    
    handleApiStatusClick() {
      // Toujours ouvrir la sidebar
      this.openSyncSidebar()
    },

    openSyncSidebar() {
      this.isSyncSidebarOpen = true
      this.loadPendingMutations()
    },

    closeSyncSidebar() {
      this.isSyncSidebarOpen = false
    },

    async loadPendingMutations() {
      try {
        // Charger les statistiques de synchronisation
        this.syncStats = await mutationService.getSyncStats()

        // Charger toutes les mutations (elles sont supprimées après synchronisation)
        const { mutations } = await import('../../services/dexieService')
        this.pendingMutations = await mutations().toArray()
      } catch (error) {
        console.error('Erreur lors du chargement des mutations en attente:', error)
      }
    },

    /**
     * Resynchronisation des propriétés depuis une date spécifique.
     * Résout le problème de "trou de sync" entre la génération de l'export
     * et la dernière sync incrémentale.
     *
     * @param {string} fromDate - Date au format 'YYYY-MM-DD' (depuis l'input date du modal)
     */
    async handleForceFullResync(fromDate) {
      if (!this.isApiAvailable) return

      try {
        // Construire la date de sync : début de journée à minuit
        const since = `${fromDate} 00:00:00`

        // 1. Réinitialiser la date de sync dans IndexedDB
        await syncService.resetEntitySyncDate('properties', since)

        // 2. Déclencher la sync intelligente (détectera que properties est outdated)
        await syncService.smartSync()

        // 3. Recharger les stats/mutations
        await this.loadPendingMutations()

      } catch (error) {
        console.error('[AppLayout] Erreur lors de la resynchronisation complète:', error)
      }
    },

    async handleForceSync() {
      if (!this.isApiAvailable) {
        return
      }

      try {
        const result = await mutationService.syncPendingMutations()
        
        // Recharger les mutations après la synchronisation
        await this.loadPendingMutations()

        // Réinitialiser les tentatives de sync si la synchronisation a réussi
        if (result.success) {
          apiStatusService.resetSyncAttempts()
        }

        return result
      } catch (error) {
        console.error('Erreur lors de la synchronisation forcée:', error)
        return {
          success: false,
          syncedCount: 0,
          error: error.message
        }
      }
    },

    onApiStatusChange(status) {
      if (status === 'sync') {
        // Le service demande une tentative de synchronisation
        this.handleAutoSync()
      } else {
        // Changement de statut de l'API
        this.isApiAvailable = status
        
        // Recharger les mutations si nécessaire
        if (this.isSyncSidebarOpen) {
          this.loadPendingMutations()
        }
      }
    },

    async handleAutoSync() {
      if (!this.isApiAvailable) {
        return
      }

      try {
        const result = await mutationService.syncPendingMutations()
        
        // Recharger les mutations après la synchronisation
        await this.loadPendingMutations()

        if (result.success && result.syncedCount > 0) {
          // Réinitialiser les tentatives
          apiStatusService.resetSyncAttempts()
        } else if (!result.success) {
          // Planifier une nouvelle tentative avec délai croissant
          const syncStats = apiStatusService.getSyncStats()
          apiStatusService.scheduleSyncAttempt(syncStats.attempts)
        }
      } catch (error) {
        console.error('Erreur lors de la synchronisation automatique:', error)
        // Planifier une nouvelle tentative
        const syncStats = apiStatusService.getSyncStats()
        apiStatusService.scheduleSyncAttempt(syncStats.attempts)
      }
    },
    
    handleToggleOffline() {
      this.toggleOfflineAction()
    },
    
    // Export dropdown methods
    toggleExportDropdown() {
      this.isExportDropdownOpen = !this.isExportDropdownOpen
    },
    
    closeExportDropdown() {
      this.isExportDropdownOpen = false
    },
    
    handleClickOutside(event) {
      // Fermer le dropdown si on clique en dehors
      const dropdown = this.$el.querySelector('.export-dropdown')
      if (dropdown && !dropdown.contains(event.target)) {
        this.closeExportDropdown()
      }
    }
  },
  mounted() {
    // Initialiser la version dans le cookie si elle n'existe pas
    initializeVersion()

    // Démarrer la détection de connectivité (health check toutes les 20s)
    apiStatusService.startChecking()

    // S'abonner aux changements de statut de l'API via le bus global
    this.apiStatusUnsubscribe = Event.listen('api-status-change', ({ isAvailable }) => {
      this.onApiStatusChange(isAvailable)
    })

    // Vérifier immédiatement le statut (avant le premier health check)
    this.isApiAvailable = apiStatusService.getStatus()

    // Charger les mutations en attente pour afficher le badge
    this.loadPendingMutations()

    // S'abonner aux événements de progression de sync incrémentale
    this.syncProgressUnsubscribe = syncService.addSyncProgressListener((progress) => {
      this.isSyncingData = progress.isSyncing
      this.syncProgress = { ...progress }

      // Afficher la page de chargement pour les syncs via export ZIP (fichier gzip)
      if (progress.method === 'export' && progress.isSyncing) {
        // Transmettre l'entité, la date du fichier export et la date de dernière sync au store
        this.$store.dispatch('initialLoading/startZipSync', {
          entity: progress.currentEntity,
          exportDate: progress.exportDate || null,
          lastSyncDate: progress.lastSyncDate || null
        })

        // Mettre à jour la phase (downloading / saving) et les octets téléchargés
        this.$store.commit('initialLoading/SET_ZIP_SYNC_PHASE', progress.phase || null)

        if (progress.phase === 'downloading') {
          this.$store.commit('initialLoading/SET_ZIP_SYNC_DOWNLOADED_BYTES', {
            downloadedBytes: progress.downloadedBytes || 0,
            totalDownloadBytes: progress.totalDownloadBytes || 0
          })
        } else if (progress.phase === 'saving') {
          // Phase "saving" : mettre à jour les compteurs d'enregistrements
          if (progress.currentEntity === 'controls') {
            this.$store.commit('initialLoading/SET_CONTROLS_TOTAL', progress.total || 0)
            this.$store.commit('initialLoading/SET_CONTROLS_LOADED', progress.loaded || 0)
          } else if (progress.currentEntity === 'properties') {
            this.$store.commit('initialLoading/SET_PROPERTIES_TOTAL', progress.total || 0)
            this.$store.commit('initialLoading/SET_PROPERTIES_LOADED', progress.loaded || 0)
          }
        }
      } else if (!progress.isSyncing) {
        this.$store.dispatch('initialLoading/stopZipSync')
      }
    })

    // Démarrer la synchronisation périodique des données (toutes les 2 min)
    syncService.startPeriodicSync()

    // Purge immédiate des lots soft-deleted et hors gestion à chaque chargement de l'app.
    // Appelle /properties/deleted sans `since` → retourne TOUS les lots à ne pas afficher
    // (deleted_at != null + managementEndedAt expirée) et les supprime d'IndexedDB.
    // Garantit qu'un lot supprimé côté API disparaît dès la prochaine ouverture de la PWA,
    // sans attendre un cycle de sync incrémentale ou un re-téléchargement du ZIP.
    syncService.syncDeletions().catch(err => {
      console.warn('[AppLayout] Purge au démarrage échouée (non bloquant) :', err)
    })
    
    // Ajouter l'écouteur pour fermer le dropdown lors d'un clic extérieur
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeUnmount() {
    // Arrêter la détection de connectivité
    apiStatusService.stopChecking()

    // Arrêter la synchronisation périodique
    syncService.stopPeriodicSync()

    // Se désabonner des changements de statut de l'API
    if (this.apiStatusUnsubscribe) {
      this.apiStatusUnsubscribe()
    }

    // Se désabonner des événements de progression de sync
    if (this.syncProgressUnsubscribe) {
      this.syncProgressUnsubscribe()
    }
    
    // Retirer l'écouteur de clic
    document.removeEventListener('click', this.handleClickOutside)
  },
  data() {
    return {
      questionSvgSrc,
      primviewWithoutIconSrc,
      pdfGuideSrc: '/guide.pdf',
      cloudIcoSrc,
      tabIcoSrc,
      // API Status
      isApiAvailable: false,
      apiStatusUnsubscribe: null,
      // Sync incrémentale — progression
      isSyncingData: false,
      syncProgress: {
        isSyncing: false,
        currentEntity: null,
        method: null,
        currentPage: 0,
        totalPages: 0,
        totalFetched: 0
      },
      syncProgressUnsubscribe: null,
      // Sync Sidebar
      isSyncSidebarOpen: false,
      pendingMutations: [],
      syncStats: {
        total: 0,
        pending: 0,
        synced: 0,
        propertiesWithToSync: 0
      },
      // Export dropdown
      isExportDropdownOpen: false
    }
  }
}
</script>
